import type { ReactNode } from 'react';
import { IconCaretRightFilled } from '@tabler/icons-react-native';
import { Box, useTheme } from 'app/shopify/theme';
import { INTERACTION } from 'app/shopify/generated/interaction';
import { ActionPressable, type ActionProps } from '../shared/ActionPressable';
import { ActionAsset, ActionLabel, ActionSpinner } from '../shared/ActionContent';

export interface CustomButtonProps extends ActionProps {
  label: string;
  /** Replaces the Partage+ badge; useful for white-label product names. */
  badge?: ReactNode;
  fullWidth?: boolean;
  /** Figma's content slot; the supplied accessibility label names custom content. */
  children?: ReactNode;
}

export function CustomButton({
  label,
  badge,
  children,
  fullWidth,
  accessibilityLabel = label,
  ...props
}: CustomButtonProps) {
  const theme = useTheme();
  const color = theme.colors['text.brand'];
  return (
    <ActionPressable
      {...props}
      accessibilityLabel={accessibilityLabel}
      fullWidth={fullWidth}
      minHeight={Number(theme.tokens.sizing['sizing.component.button.height.large-icn-button'])}
      paddingHorizontal={theme.spacing['spacing.general.x6']}
      paddingVertical={theme.spacing['spacing.general.x3']}
      appearance={({ pressed, hovered, disabled }) => ({
        backgroundColor: theme.colors['surface.brand.secondary-5.subtle'],
        borderColor: theme.colors['border.brand'],
        borderWidth: theme.borderWidths['border-width.subtle'],
        opacity: disabled ? INTERACTION.opacity['040'] : 1,
        overlayColor:
          pressed || hovered
            ? theme.colors[pressed ? 'state-layer.press-brand' : 'state-layer.hover-brand']
            : undefined,
      })}
    >
      {({ loading }) => (
        <>
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            gap="spacing.general.x2"
            maxWidth="100%"
            style={{ opacity: loading ? 0 : 1 }}
            aria-hidden={loading}
          >
            {children ?? (
              <>
                <ActionLabel label={label} color={color} small={false} padding="spacing.general.0" />
                {badge ?? (
                  <ActionAsset
                    source={require('../shared/assets/partage-plus-badge.svg')}
                    width={66.3276}
                    height={19.0004}
                  />
                )}
                <IconCaretRightFilled
                  size={Number(theme.tokens.sizing['sizing.icon.default'])}
                  color={color}
                  accessible={false}
                />
              </>
            )}
          </Box>
          {loading ? (
            <Box position="absolute" top={0} bottom={0} left={0} right={0} alignItems="center" justifyContent="center">
              <ActionSpinner color={color} />
            </Box>
          ) : null}
        </>
      )}
    </ActionPressable>
  );
}
